import { useEffect, useId, useRef, useState, KeyboardEvent } from "react";
import { useSelector } from "react-redux";
import {
  AvatarResponse,
  choosePhoto,
  deletePhoto,
  EMPTY_AVATAR,
  MAX_AVATAR_PHOTOS,
  putCharacter,
  resetAvatar,
  uploadPhoto,
} from "../../api/avatar.api";
import { AvatarArt } from "../../avatar/AvatarArt";
import {
  AVATAR_OPTIONS,
  DEFAULT_AVATAR,
  HAIR_BY_BASE,
  isHumanBase as isHuman,
  normalizeAvatar,
  randomAvatar,
} from "../../avatar/avatarOptions";
import type { AvatarConfig, AvatarKey, AvatarOption, HumanBase } from "../../avatar/types";
import { UserAvatar } from "../../avatar/UserAvatar";
import { useMyAvatar } from "../../avatar/useMyAvatar";
import { LayoutAdminCafe, LayoutUserCafe } from "../../layout/layout_cafe";
import { State } from "../../reducer";
import { isAdminLike } from "../../utils/access";
import "./avatarEditor.css";

// ---------- photo pipeline (exported for reuse/tests) ----------

export const AVATAR_PX = 256;
export const MAX_AVATAR_BYTES = 200 * 1024;
const MAX_SOURCE_BYTES = 15 * 1024 * 1024;
const QUALITIES = [0.92, 0.85, 0.78, 0.7, 0.6, 0.5, 0.4, 0.3];

export class AvatarPhotoError extends Error {}

/** Decoded size of a base64 data URL in bytes. */
export const dataUrlBytes = (url: string) => {
  const b64 = url.slice(url.indexOf(",") + 1);
  const pad = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - pad;
};

type Decoded = { source: CanvasImageSource; width: number; height: number; done: () => void };

const decode = async (file: File): Promise<Decoded> => {
  const cib = (window as any).createImageBitmap as ((f: Blob) => Promise<ImageBitmap>) | undefined;
  if (typeof cib === "function") {
    const bmp = await cib(file);
    return { source: bmp, width: bmp.width, height: bmp.height, done: () => bmp.close?.() };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, done: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
};

/** Center-crop to a square, resize to 256×256, encode WebP (JPEG fallback) under 200KB. */
export async function prepareAvatarPhoto(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new AvatarPhotoError("ไฟล์นี้ไม่ใช่รูปภาพ — เลือกไฟล์ JPG, PNG หรือ WebP");
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new AvatarPhotoError("ไฟล์ใหญ่เกิน 15MB — เลือกรูปที่เล็กลง");
  }
  let img: Decoded;
  try {
    img = await decode(file);
  } catch {
    throw new AvatarPhotoError("เปิดรูปนี้ไม่ได้ — ลองบันทึกเป็น JPG หรือ PNG แล้วอัปโหลดใหม่");
  }
  try {
    if (!img.width || !img.height) throw new AvatarPhotoError("เปิดรูปนี้ไม่ได้ — ลองรูปอื่น");
    const canvas = document.createElement("canvas");
    canvas.width = AVATAR_PX;
    canvas.height = AVATAR_PX;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new AvatarPhotoError("เบราว์เซอร์นี้ย่อรูปไม่ได้ — ลองเปิดด้วยเบราว์เซอร์อื่น");
    const side = Math.min(img.width, img.height);
    const sx = Math.round((img.width - side) / 2);
    const sy = Math.round((img.height - side) / 2);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = "#ffffff"; // transparent PNGs would turn black in JPEG
    ctx.fillRect(0, 0, AVATAR_PX, AVATAR_PX);
    ctx.drawImage(img.source, sx, sy, side, side, 0, 0, AVATAR_PX, AVATAR_PX);

    for (const mime of ["image/webp", "image/jpeg"]) {
      for (const q of QUALITIES) {
        const url = canvas.toDataURL(mime, q);
        if (!url.startsWith(`data:${mime};`)) break; // encoder unsupported → next format
        if (dataUrlBytes(url) <= MAX_AVATAR_BYTES) return url;
      }
    }
    throw new AvatarPhotoError("บีบอัดรูปให้ต่ำกว่า 200KB ไม่ได้ — ลองรูปอื่น");
  } finally {
    img.done();
  }
}

// ---------- editor ----------

// Contract v3 (docs/AVATAR-CONTRACT-v3.md): boy/girl bases add skin/hair/hairColor.
type HumanKey = "skin" | "hair" | "hairColor";
type Draft = AvatarConfig;
type Segment = HumanBase | "toon";

const HUMAN_KEYS: readonly HumanKey[] = ["skin", "hair", "hairColor"];
const SHARED_KEYS: readonly AvatarKey[] = ["color", "eyes", "mouth", "cheeks", "hat", "accessory", "holding", "background"];

const SEGMENTS: readonly { id: Segment; label: string }[] = [
  { id: "boy", label: "ผู้ชาย" },
  { id: "girl", label: "ผู้หญิง" },
  { id: "toon", label: "ตัวการ์ตูน" },
];

const segmentOf = (base: string): Segment => (isHuman(base) ? base : "toon");
const TOON_BASES = AVATAR_OPTIONS.base.filter((o) => !isHuman(o.id));
const hairOptions = (base: HumanBase): AvatarOption[] =>
  HAIR_BY_BASE[base].map((id) => AVATAR_OPTIONS.hair.find((o) => o.id === id) ?? { id, label: id });
const has = (key: AvatarKey, id: string | undefined) => !!id && AVATAR_OPTIONS[key].some((o) => o.id === id);
const pickFrom = <T,>(list: readonly T[]): T => {
  const r = Number(Math.random());
  return list[Number.isFinite(r) ? Math.min(list.length - 1, Math.max(0, Math.floor(r * list.length))) : 0];
};

/** Make a draft consistent with its base: humans always carry skin/hair/hairColor, cartoons never do. */
const fitToBase = (d: Draft, base: string, resetHair = false): Draft => {
  const next: Draft = { ...d, base };
  if (!isHuman(base)) {
    for (const k of HUMAN_KEYS) delete next[k];
    return next;
  }
  if (!has("skin", next.skin)) next.skin = DEFAULT_AVATAR.skin;
  if (!has("hairColor", next.hairColor)) next.hairColor = DEFAULT_AVATAR.hairColor;
  if (resetHair || !HAIR_BY_BASE[base].includes(next.hair ?? "")) next.hair = HAIR_BY_BASE[base][0];
  return next;
};

/** Only the contract keys, and skin/hair/hairColor only for human bases. */
const toPayload = (d: Draft): AvatarConfig => {
  const keys: readonly AvatarKey[] = isHuman(d.base) ? ["base", ...SHARED_KEYS, ...HUMAN_KEYS] : ["base", ...SHARED_KEYS];
  return Object.fromEntries(keys.filter((k) => d[k] !== undefined).map((k) => [k, d[k]])) as unknown as AvatarConfig;
};

const KEY_LABEL: Record<AvatarKey, string> = {
  base: "ตัวละคร",
  color: "สี",
  eyes: "ดวงตา",
  mouth: "ปาก",
  cheeks: "แก้ม",
  hat: "หมวก",
  accessory: "เครื่องประดับ",
  holding: "ของในมือ",
  background: "พื้นหลัง",
  skin: "ผิว",
  hair: "ทรงผม",
  hairColor: "สีผม",
};
const labelFor = (key: AvatarKey, human: boolean) => (human && key === "color" ? "สีเสื้อ" : KEY_LABEL[key]);

type Tab = "character" | "photo";
type Busy = "save" | "reset" | "prepare" | "upload" | `choose-${number}` | `delete-${number}` | null;
type Notice = { kind: "ok" | "error"; text: string } | null;

const netErrorText = (verb: string, e: any) => {
  const status = e?.response?.status;
  if (status === 400) return `${verb}ไม่สำเร็จ — เซิร์ฟเวอร์ไม่รับข้อมูลนี้ ลองใหม่อีกครั้ง`;
  if (status === 404) return `${verb}ไม่สำเร็จ — ไม่พบรูปนี้แล้ว ลองรีเฟรชหน้า`;
  if (status === 502) return `${verb}ไม่สำเร็จ — ระบบเก็บรูปขัดข้อง ลองใหม่อีกครั้ง`;
  return `${verb}ไม่สำเร็จ — ตรวจสอบอินเทอร์เน็ตแล้วลองอีกครั้ง`;
};

const shortDate = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("th-TH", { day: "numeric", month: "short" });
};

interface AvatarEditorProps {
  userId: number;
  name?: string;
}

export function AvatarEditor({ userId, name }: AvatarEditorProps) {
  const { avatar, status, reload, setAvatar } = useMyAvatar(userId);
  const [tab, setTab] = useState<Tab>("character");
  const [draft, setDraft] = useState<Draft>(() => fitToBase(DEFAULT_AVATAR, DEFAULT_AVATAR.base));
  // Cartoon base to return to when leaving ผู้ชาย/ผู้หญิง.
  const lastToon = useRef<string>(isHuman(DEFAULT_AVATAR.base) ? TOON_BASES[0].id : DEFAULT_AVATAR.base);
  const [pending, setPending] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [hydrated, setHydrated] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const uid = useId();
  const tabId = (t: Tab) => `${uid}-tab-${t}`;
  const panelId = (t: Tab) => `${uid}-panel-${t}`;

  // Seed the editor once from the saved avatar (config is kept even in photo mode).
  useEffect(() => {
    if (hydrated || status !== "ready" || !avatar) return;
    setHydrated(true);
    if (avatar.config) {
      const loaded = normalizeAvatar(avatar.config);
      if (!isHuman(loaded.base)) lastToon.current = loaded.base;
      setDraft(fitToBase(loaded, loaded.base));
    }
    if (avatar.mode === "photo") setTab("photo");
  }, [avatar, status, hydrated]);

  const saved: AvatarResponse = avatar ?? EMPTY_AVATAR;
  const photos = saved.photos ?? [];
  const activePhotoId = saved.mode === "photo" ? saved.photo?.id ?? null : null;
  // Stay on the loader until the saved avatar has been copied into the draft.
  const loading = status === "idle" || status === "loading" || (status === "ready" && !hydrated);

  const run = async (b: Busy, verb: string, ok: string, call: () => Promise<AvatarResponse>) => {
    setBusy(b);
    setNotice(null);
    try {
      setAvatar(await call());
      setNotice({ kind: "ok", text: ok });
      return true;
    } catch (e) {
      setNotice({ kind: "error", text: netErrorText(verb, e) });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const switchTab = (t: Tab) => {
    setTab(t);
    setNotice(null);
  };

  const segment = segmentOf(draft.base);
  const human = isHuman(draft.base);

  const pick = (key: AvatarKey, id: string) => {
    if (key === "base" && !isHuman(id)) lastToon.current = id;
    setDraft((d) => (key === "base" ? fitToBase(d, id) : { ...d, [key]: id }));
    setNotice(null);
  };

  const chooseSegment = (seg: Segment) => {
    if (seg === segment) return;
    setDraft((d) => fitToBase(d, seg === "toon" ? lastToon.current : seg, true));
    setNotice(null);
  };

  const onSegmentKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = SEGMENTS.findIndex((x) => x.id === segment);
    const n = SEGMENTS.length;
    const to: Record<string, number> = {
      ArrowRight: (i + 1) % n, ArrowDown: (i + 1) % n, ArrowLeft: (i - 1 + n) % n, ArrowUp: (i - 1 + n) % n, Home: 0, End: n - 1,
    };
    if (!(e.key in to)) return;
    e.preventDefault();
    const next = SEGMENTS[to[e.key]].id;
    chooseSegment(next);
    document.getElementById(`${uid}-seg-${next}`)?.focus();
  };

  const randomize = () => {
    const r = randomAvatar();
    const base = human ? draft.base : isHuman(r.base) ? pickFrom(TOON_BASES).id : r.base;
    const next: Draft = { ...r, base };
    if (isHuman(base)) next.hair = pickFrom(HAIR_BY_BASE[base]);
    else lastToon.current = base;
    setDraft(fitToBase(next, base));
    setNotice(null);
  };

  const pickerKeys: readonly AvatarKey[] = human ? [...HUMAN_KEYS, ...SHARED_KEYS] : ["base", ...SHARED_KEYS];
  const optionsOf = (key: AvatarKey): readonly AvatarOption[] =>
    key === "hair" && isHuman(draft.base) ? hairOptions(draft.base) : key === "base" ? TOON_BASES : AVATAR_OPTIONS[key];

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy("prepare");
    setNotice(null);
    setPending(null);
    try {
      setPending(await prepareAvatarPhoto(file));
    } catch (e) {
      setNotice({ kind: "error", text: e instanceof AvatarPhotoError ? e.message : "เปิดรูปนี้ไม่ได้ — ลองรูปอื่น" });
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const saveCharacter = () => run("save", "บันทึก", "บันทึกแล้ว", () => putCharacter(toPayload(draft)));

  const uploadPending = async () => {
    if (!pending) return;
    const image = pending;
    if (await run("upload", "อัปโหลด", "อัปโหลดแล้ว", () => uploadPhoto(image))) setPending(null);
  };

  const activatePhoto = (id: number) => {
    setPending(null);
    return run(`choose-${id}`, "เปลี่ยนรูป", "ใช้รูปนี้แล้ว", () => choosePhoto(id));
  };

  const removePhoto = (id: number) => {
    const msg = id === activePhotoId
      ? "ลบรูปนี้ออกจากประวัติ? รูปโปรไฟล์จะกลับไปเป็นตัวอักษรย่อ"
      : "ลบรูปนี้ออกจากประวัติใช่ไหม?";
    if (!window.confirm(msg)) return;
    return run(`delete-${id}`, "ลบรูป", "ลบรูปแล้ว", () => deletePhoto(id));
  };

  const reset = () => {
    if (!window.confirm("ลบรูปโปรไฟล์แล้วกลับไปใช้ตัวอักษรย่อใช่ไหม? (ตัวละครและรูปเก่ายังเก็บไว้)")) return;
    setPending(null);
    return run("reset", "ลบรูปโปรไฟล์", "ลบรูปโปรไฟล์แล้ว", resetAvatar);
  };

  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
    e.preventDefault();
    const next: Tab = e.key === "Home" ? "character" : e.key === "End" ? "photo" : tab === "character" ? "photo" : "character";
    switchTab(next);
    document.getElementById(tabId(next))?.focus();
  };

  const shownPhoto = pending ?? (saved.mode === "photo" ? saved.photo?.url ?? null : null);
  const atLimit = photos.length >= MAX_AVATAR_PHOTOS;

  return (
    <div className="avatar-editor">
      <header className="ae-head">
        <p className="eyebrow-v2">โปรไฟล์</p>
        <h1 className="ae-title">แต่งรูปโปรไฟล์</h1>
        <p className="ae-sub">เลือกตัวละครน่ารัก ๆ หรือใช้รูปของคุณเอง</p>
      </header>

      {loading ? (
        <p className="ae-loading" role="status">กำลังโหลด…</p>
      ) : (
        <div className="ae-grid">
          <section className="ae-stage card-v2" aria-label="ตัวอย่าง">
            <div className="ae-preview" data-testid="avatar-preview">
              {tab === "character" ? (
                <AvatarArt config={draft} size={200} title="ตัวอย่างตัวละคร" />
              ) : shownPhoto ? (
                <img src={shownPhoto} alt="ตัวอย่างรูปโปรไฟล์" width={200} height={200} />
              ) : (
                <UserAvatar avatar={null} name={name} size={200} />
              )}
            </div>
            <p className="ae-caption">
              {tab === "photo" && pending
                ? `ตัวอย่างก่อนอัปโหลด · ${Math.max(1, Math.round(dataUrlBytes(pending) / 1024))} KB`
                : name || "ตัวอย่าง"}
            </p>

            {status === "error" && (
              <p className="ae-msg ae-msg-error" role="alert">
                โหลดรูปโปรไฟล์ที่บันทึกไว้ไม่สำเร็จ{" "}
                <button type="button" className="ae-link" onClick={() => void reload()}>ลองใหม่</button>
              </p>
            )}
            <div aria-live="polite">
              {notice?.kind === "ok" && <p className="ae-msg ae-msg-ok" role="status">{notice.text}</p>}
            </div>
            {notice?.kind === "error" && <p className="ae-msg ae-msg-error" role="alert">{notice.text}</p>}

            <div className="ae-actions">
              {tab === "character" ? (
                <>
                  <button type="button" className="btn-v2-primary btn-v2-lg ae-save" disabled={!!busy} onClick={saveCharacter}>
                    {busy === "save" ? "กำลังบันทึก…" : "บันทึก"}
                  </button>
                  <button
                    type="button"
                    className="btn-v2-secondary btn-v2-md"
                    disabled={!!busy}
                    onClick={randomize}
                  >
                    สุ่ม
                  </button>
                </>
              ) : (
                <button type="button" className="btn-v2-primary btn-v2-lg ae-save" disabled={!!busy || !pending} onClick={uploadPending}>
                  {busy === "upload" ? "กำลังอัปโหลด…" : "อัปโหลดรูปนี้"}
                </button>
              )}
              {saved.mode && (
                <button type="button" className="btn-v2-ghost btn-v2-md ae-delete" disabled={!!busy} onClick={reset}>
                  {busy === "reset" ? "กำลังลบ…" : "ลบรูปโปรไฟล์"}
                </button>
              )}
            </div>
          </section>

          <section className="ae-controls">
            <div className="ae-tabs" role="tablist" aria-label="วิธีตั้งรูปโปรไฟล์">
              {(["character", "photo"] as Tab[]).map((t) => (
                <button
                  key={t}
                  id={tabId(t)}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  aria-controls={panelId(t)}
                  tabIndex={tab === t ? 0 : -1}
                  className="ae-tab"
                  onClick={() => switchTab(t)}
                  onKeyDown={onTabKey}
                >
                  {t === "character" ? "แต่งตัวละคร" : "รูปของฉัน"}
                </button>
              ))}
            </div>

            {tab === "character" ? (
              <div id={panelId("character")} role="tabpanel" aria-labelledby={tabId("character")} className="ae-panel">
                <div className="ae-segbar" role="radiogroup" aria-label="แบบตัวละคร">
                  {SEGMENTS.map((sg) => {
                    const on = segment === sg.id;
                    return (
                      <button
                        key={sg.id}
                        id={`${uid}-seg-${sg.id}`}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        tabIndex={on ? 0 : -1}
                        className="ae-seg"
                        data-segment={sg.id}
                        onClick={() => chooseSegment(sg.id)}
                        onKeyDown={onSegmentKey}
                      >
                        {sg.label}
                      </button>
                    );
                  })}
                </div>
                {pickerKeys.map((key) => (
                  <fieldset key={key} className={`ae-group ae-group-${key}`}>
                    <legend className="ae-legend">{labelFor(key, human)}</legend>
                    <div role="radiogroup" aria-label={labelFor(key, human)} className="ae-options">
                      {optionsOf(key).map((opt) => {
                        const checked = draft[key] === opt.id;
                        return (
                          <label key={opt.id} className={`ae-option${checked ? " is-checked" : ""}`} title={opt.label}>
                            <input
                              type="radio"
                              className="ae-radio"
                              name={`${uid}-${key}`}
                              value={opt.id}
                              checked={checked}
                              aria-label={opt.label}
                              onChange={() => pick(key, opt.id)}
                            />
                            <span className="ae-thumb" aria-hidden="true">
                              <AvatarArt config={key === "base" ? fitToBase(draft, opt.id) : { ...draft, [key]: opt.id }} size={56} />
                            </span>
                            <span className="ae-option-label" aria-hidden="true">{opt.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
              </div>
            ) : (
              <div id={panelId("photo")} role="tabpanel" aria-labelledby={tabId("photo")} className="ae-panel">
                <div className="ae-drop">
                  <input
                    ref={fileInput}
                    id={`${uid}-file`}
                    type="file"
                    accept="image/*"
                    className="ae-file"
                    disabled={busy === "prepare" || busy === "upload"}
                    onChange={(e) => void onFile(e.target.files?.[0])}
                  />
                  <label htmlFor={`${uid}-file`} className="btn-v2-secondary btn-v2-md ae-file-btn">
                    {busy === "prepare" ? "กำลังเตรียมรูป…" : "เลือกรูปจากเครื่อง"}
                  </label>
                  <p className="ae-hint">
                    รูปจะถูกตัดตรงกลางเป็นสี่เหลี่ยมจัตุรัส ย่อเหลือ 256×256 และบีบอัดไม่เกิน 200KB
                    ดูตัวอย่างก่อน แล้วกด “อัปโหลดรูปนี้”
                  </p>
                  {atLimit && (
                    <p className="ae-warn">
                      มีรูปครบ {MAX_AVATAR_PHOTOS} รูปแล้ว — อัปโหลดรูปใหม่จะลบรูปที่เก่าที่สุดออกอัตโนมัติ
                    </p>
                  )}
                </div>

                <h2 className="ae-legend" id={`${uid}-hist`}>
                  รูปที่เคยอัปโหลด ({photos.length}/{MAX_AVATAR_PHOTOS})
                </h2>
                {photos.length === 0 ? (
                  <p className="ae-hint">ยังไม่มีรูป — อัปโหลดรูปแรกได้เลย</p>
                ) : (
                  <ul className="ae-history" aria-labelledby={`${uid}-hist`}>
                    {photos.map((p, i) => {
                      const active = p.id === activePhotoId;
                      return (
                        <li key={p.id} className={`ae-photo${active ? " is-active" : ""}`} aria-current={active ? "true" : undefined}>
                          <span className="ae-photo-img">
                            <img src={p.url} alt={`รูปที่ ${i + 1}${active ? " (ใช้อยู่)" : ""}`} loading="lazy" width={96} height={96} />
                            {active && <span className="ae-badge" aria-hidden="true">ใช้อยู่</span>}
                          </span>
                          <span className="ae-photo-date">{shortDate(p.createdAt)}</span>
                          <span className="ae-photo-actions">
                            <button
                              type="button"
                              className="btn-v2-primary btn-v2-sm"
                              disabled={!!busy || active}
                              onClick={() => void activatePhoto(p.id)}
                            >
                              {busy === `choose-${p.id}` ? "กำลังเปลี่ยน…" : "ใช้รูปนี้"}
                            </button>
                            <button
                              type="button"
                              className="btn-v2-ghost btn-v2-sm ae-delete"
                              disabled={!!busy}
                              onClick={() => void removePhoto(p.id)}
                            >
                              {busy === `delete-${p.id}` ? "กำลังลบ…" : "ลบ"}
                            </button>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

/** Routed page: picks the layout the same way other user/admin pages do. */
export default function AvatarEditorPage() {
  const user = useSelector((state: State) => state.user);
  const body = <AvatarEditor userId={user?.id} name={user?.name} />;
  return isAdminLike(user?.role) ? <LayoutAdminCafe>{body}</LayoutAdminCafe> : <LayoutUserCafe>{body}</LayoutUserCafe>;
}
